import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../student/Navbar';
import AdminSidebar from './AdminSidebar';
import Loader from '../components/Loader';
import { motion } from 'framer-motion';
import { 
  FaEdit, 
  FaUser, 
  FaTooth, 
  FaFileMedical, 
  FaClipboardCheck, 
  FaChartLine,
  FaFilter,
  FaSearch,
  FaDownload,
  FaEye,
  FaClock,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle
} from 'react-icons/fa';
import axios from 'axios';

// Website color palette
const websiteColorPalette = {
  primary: '#0077B6',
  secondary: '#20B2AA',
  background: '#FFFFFF',
  text: '#333333',
  accent: '#28A745'
};

const EditTrackingDashboard = () => {
  const { user, token } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editData, setEditData] = useState({
    totalEdits: 0,
    editsByType: {},
    editsByStudent: {},
    recentEdits: [],
    editTrends: [],
    errorCount: 0,
    successRate: 0
  });
  const [filters, setFilters] = useState({
    dateRange: '7d',
    entityType: 'all',
    studentId: 'all',
    editType: 'all'
  });
  const [selectedEdit, setSelectedEdit] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    fetchEditData();
  }, [filters]);

  const fetchEditData = async () => {
    try {
      setLoading(true);
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const params = new URLSearchParams();
      if (filters.dateRange !== 'all') params.append('dateRange', filters.dateRange);
      if (filters.entityType !== 'all') params.append('entityType', filters.entityType);
      if (filters.studentId !== 'all') params.append('studentId', filters.studentId);
      if (filters.editType !== 'all') params.append('editType', filters.editType);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/analytics/edit-tracking?${params.toString()}`,
        config
      );

      setEditData(response.data);
    } catch (err) {
      setError('Failed to load edit tracking data');
      console.error('Edit tracking error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getEntityIcon = (entityType) => {
    switch (entityType) {
      case 'patient': return <FaUser className="text-blue-500" />;
      case 'tooth_chart': return <FaTooth className="text-green-500" />;
      case 'treatment_sheet': return <FaFileMedical className="text-purple-500" />;
      case 'review': return <FaClipboardCheck className="text-orange-500" />;
      default: return <FaEdit className="text-gray-500" />;
    }
  };

  const getEditTypeColor = (editType) => {
    switch (editType) {
      case 'field_edit': return 'bg-blue-100 text-blue-800';
      case 'bulk_edit': return 'bg-purple-100 text-purple-800';
      case 'file_edit': return 'bg-green-100 text-green-800';
      case 'status_change': return 'bg-yellow-100 text-yellow-800';
      case 'entity_creation': return 'bg-emerald-100 text-emerald-800';
      case 'entity_deletion': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getChangeTypeIcon = (changeType) => {
    switch (changeType) {
      case 'added': return <FaCheckCircle className="text-green-500" />;
      case 'removed': return <FaTimesCircle className="text-red-500" />;
      case 'modified': return <FaEdit className="text-blue-500" />;
      default: return <FaClock className="text-gray-500" />;
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="flex h-screen bg-gray-50">
        <AdminSidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Navbar onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
          <div className="flex-1 overflow-y-auto p-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <FaExclamationTriangle className="text-red-500 mr-2" />
                <span className="text-red-700">{error}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <AdminSidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h1 className={`text-2xl sm:text-3xl font-bold text-[${websiteColorPalette.primary}]`}>
                    Edit Tracking Dashboard
                  </h1>
                  <p className="text-gray-600 mt-1">Monitor all student edits and modifications across the system</p>
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={fetchEditData}
                    className={`px-4 py-2 bg-[${websiteColorPalette.primary}] text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center`}
                  >
                    <FaDownload className="inline mr-2" />
                    Export Data
                  </button>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                  <select
                    value={filters.dateRange}
                    onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="1d">Last 24 hours</option>
                    <option value="7d">Last 7 days</option>
                    <option value="30d">Last 30 days</option>
                    <option value="90d">Last 90 days</option>
                    <option value="all">All time</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Entity Type</label>
                  <select
                    value={filters.entityType}
                    onChange={(e) => setFilters({ ...filters, entityType: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Types</option>
                    <option value="patient">Patients</option>
                    <option value="tooth_chart">Tooth Charts</option>
                    <option value="treatment_sheet">Treatment Sheets</option>
                    <option value="review">Reviews</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Student</label>
                  <select
                    value={filters.studentId}
                    onChange={(e) => setFilters({ ...filters, studentId: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Students</option>
                    {/* Add student options dynamically */}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Edit Type</label>
                  <select
                    value={filters.editType}
                    onChange={(e) => setFilters({ ...filters, editType: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Types</option>
                    <option value="field_edit">Field Edits</option>
                    <option value="bulk_edit">Bulk Edits</option>
                    <option value="file_edit">File Edits</option>
                    <option value="status_change">Status Changes</option>
                    <option value="entity_creation">Creations</option>
                    <option value="entity_deletion">Deletions</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
              >
                <div className="flex items-center">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <FaEdit className="text-blue-600 text-xl" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Edits</p>
                    <p className="text-2xl font-bold text-gray-900">{editData.totalEdits || 0}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
              >
                <div className="flex items-center">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <FaCheckCircle className="text-green-600 text-xl" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Success Rate</p>
                    <p className="text-2xl font-bold text-gray-900">{editData.successRate || 0}%</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
              >
                <div className="flex items-center">
                  <div className="p-3 bg-red-100 rounded-lg">
                    <FaExclamationTriangle className="text-red-600 text-xl" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Errors</p>
                    <p className="text-2xl font-bold text-gray-900">{editData.errorCount || 0}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
              >
                <div className="flex items-center">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <FaChartLine className="text-purple-600 text-xl" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Active Students</p>
                    <p className="text-2xl font-bold text-gray-900">{editData.editsByStudent ? Object.keys(editData.editsByStudent).length : 0}</p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Edits by Type Chart */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Edits by Entity Type</h3>
              <div className="space-y-4">
                {editData.editsByType && Object.entries(editData.editsByType).map(([entityType, count]) => (
                  <div key={entityType} className="flex items-center justify-between">
                    <div className="flex items-center">
                      {getEntityIcon(entityType)}
                      <span className="ml-3 text-sm font-medium text-gray-900 capitalize">
                        {entityType ? entityType.replace('_', ' ') : 'Unknown'}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${editData.totalEdits > 0 ? (count / editData.totalEdits) * 100 : 0}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-900">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Edits Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Edits</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Entity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Edit Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Student
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Changes
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Timestamp
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {editData.recentEdits && editData.recentEdits.map((edit, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getEntityIcon(edit.entityType)}
                            <span className="ml-2 text-sm font-medium text-gray-900">
                              {edit.entityType ? edit.entityType.replace('_', ' ') : 'Unknown'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getEditTypeColor(edit.editType)}`}>
                            {edit.editType ? edit.editType.replace('_', ' ') : 'Unknown'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {edit.studentName || 'Unknown'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getChangeTypeIcon(edit.changeType)}
                            <span className="ml-2 text-sm text-gray-900">
                              {edit.fieldName ? `${edit.fieldName}: ${edit.changeType}` : edit.changeType}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatTimestamp(edit.timestamp)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => {
                              setSelectedEdit(edit);
                              setShowEditModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <FaEye />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Edit Details Modal */}
            {showEditModal && selectedEdit && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-96 overflow-y-auto">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Edit Details</h3>
                    <button
                      onClick={() => setShowEditModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <FaTimesCircle />
                    </button>
                  </div>
                  <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Entity Type</label>
                <p className="text-sm text-gray-900">{selectedEdit.entityType}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Edit Type</label>
                <p className="text-sm text-gray-900">{selectedEdit.editType}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Student</label>
                <p className="text-sm text-gray-900">{selectedEdit.studentName || 'Unknown'}</p>
              </div>
              {selectedEdit.fieldName && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Field</label>
                  <p className="text-sm text-gray-900">{selectedEdit.fieldName}</p>
                </div>
              )}
              {selectedEdit.oldValue && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Old Value</label>
                  <p className="text-sm text-gray-900 bg-red-50 p-2 rounded">{selectedEdit.oldValue}</p>
                </div>
              )}
              {selectedEdit.newValue && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">New Value</label>
                  <p className="text-sm text-gray-900 bg-green-50 p-2 rounded">{selectedEdit.newValue}</p>
                </div>
              )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Timestamp</label>
                      <p className="text-sm text-gray-900">{formatTimestamp(selectedEdit.timestamp)}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditTrackingDashboard;

